// @ts-check
import {
  FieldValue,
  GeoPoint,
  Timestamp,
  DocumentReference,
} from 'firebase/firestore';

/* This file has been copy/pasted from labs-common since the package is not frontend compatible.
Changes to this file should also be made in labs-common. */

/**
 * @typedef {import('firebase/firestore').DocumentData} DocumentData
 * @typedef {import('firebase/firestore').FirestoreDataConverter<any, DocumentData>} FirestoreDataConverter
 * @typedef {import('firebase/firestore').QueryDocumentSnapshot} QueryDocumentSnapshot
 * @typedef {import('firebase/firestore').SnapshotOptions} SnapshotOptions
 */


/**
 * Checks if a given object is a GeoPoint-like object.
 * @param {any} obj - The object to check.
 * @return {boolean} - True if the object is GeoPoint-like, false otherwise.
 */
function isGeoPointLike(obj) {
  if (Object.keys(obj).length !== 2) {
    return false;
  }

  let validLat;
  let validLng;
  try {
    validLat = !isNaN(Number(obj.latitude));
    validLng = !isNaN(Number(obj.longitude));
  } catch (error) {
    console.warn('Could not explicitly cast value to number', obj);
    return false;
  }

  return validLat && validLng;
}

/**
 * Checks if a given value is a Firestore supported type.
 * @param {any} value - The value to check.
 * @return {boolean} - True if the value is a Firestore supported type, false otherwise.
 */
function isFirestoreSupportedType(value) {
  return (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Array ||
    value instanceof Object ||
    value instanceof Date ||
    value instanceof Timestamp ||
    value instanceof GeoPoint ||
    value instanceof Blob ||
    value instanceof DocumentReference ||
    value instanceof FieldValue
  );
}

/**
 * Converts data to Firestore format.
 * @param {any} obj - The object to convert.
 * @return {any}
 */
const toFirestore = (obj) => {
  if (obj === null) return null;
  if (typeof obj !== 'object') return obj;

  const convertToFirestoreObject = (obj) => {
    const newObj = {...obj};

    Object.keys(newObj).forEach((key) => {
      if (newObj[key] == null) { // Undefined/null to null
        newObj[key] = null;
      } else if (newObj[key] instanceof FieldValue) { // Pass FieldValue sentinels (deleteField, etc.) through unchanged
        // no-op
      } else if (newObj[key] instanceof Date) { // Date to Timestamp
        newObj[key] = Timestamp.fromDate(newObj[key]);
      } else if (isGeoPointLike(newObj[key])) { // {latitude, longitude} to GeoPoint
        newObj[key] = new GeoPoint(Number(newObj[key].latitude), Number(newObj[key].longitude));
      } else if (Array.isArray(newObj[key])) { // Recurse into nested arrays
        newObj[key] = newObj[key].map((item) => toFirestore(item));
      } else if (typeof newObj[key] === 'object') { // Recurse into nested objects
        newObj[key] = toFirestore(newObj[key]);
      } else if (!isFirestoreSupportedType(newObj[key])) { // Unsupported types
        throw new Error(`Unsupported data type encountered for key "${key}": ${typeof newObj[key]}`);
      }
    });
    return newObj;
  };
  return convertToFirestoreObject(obj);
};

/**
 * Converts a Firestore document snapshot
 * @param {QueryDocumentSnapshot} snapshot
 * @param {SnapshotOptions} [options]
 * @return {any}
 */
const fromFirestore = (snapshot, options) => {
  // Converts Firestore data to regular object
  const convertFirestoreData = (/** @type {DocumentData} */ data) => {
    if (data == null) return null;
    if (Array.isArray(data)) {
      return data.map((item) => convertFirestoreData(item));
    }

    if (typeof data === 'object') {
      const newObj = {...data};
      Object.keys(newObj).forEach((key) => {
        if (newObj[key] instanceof GeoPoint) { // GeoPoint to {latitude, longitude}
          newObj[key] = {latitude: newObj[key].latitude, longitude: newObj[key].longitude};
        } else if (newObj[key] instanceof Timestamp) { // Timestamp to Date
          newObj[key] = newObj[key].toDate();
        } else if (Array.isArray(newObj[key])) { // Recurse into nested arrays
          newObj[key] = newObj[key].map((item) => convertFirestoreData(item));
        } else if (typeof newObj[key] === 'object') { // Recurse into nested objects. Null has already been handled.
          newObj[key] = convertFirestoreData(newObj[key]);
        }
      });
      return newObj;
    }
    return data;
  };

  return convertFirestoreData(snapshot.data(options));
};

/** @type {FirestoreDataConverter} */
export const converter = {
  toFirestore: (data) => toFirestore(data),
  fromFirestore,
};
